import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Flex,
  Text,
  Image,
  Heading,
  Button,
  Container,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
  Stack,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  HStack,
} from "@chakra-ui/react";

import { FaUser, FaArrowLeft } from "react-icons/fa";
import { FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import { MdCurrencyRupee } from "react-icons/md";
import { FaMapLocationDot } from "react-icons/fa6";
import { StarIcon, ChatIcon } from "@chakra-ui/icons";
import { getCustomerBooking } from "../../api-services";
import { NavLink } from "react-router-dom";
import Rating from "../../components/rating"; // Update the path if necessary
import SpinnerComponent from "../../components/Spinner";
import NodataFound from "../../components/NoDataFound";
// Provider Details
import provider from "../../assets/images/customer/booking-history/customer-img.svg";
import { BookingDetails } from './BookingDetails'

const formatAddress = (address) => {
  if (!address) return "Address not available";
  const { address_line_one, address_line_two, city, state, pincode } = address;
  return `${address_line_one}${address_line_two ? `, ${address_line_two}` : ""
    }, ${city}, ${state} - ${pincode}`;
};

const CustomerBookingHistory = () => {
  const {
    secondryText,
    primaryText,
    footerColor,
    headingColor,
    bodyTextColor,
    activeBg,
    mediumGray,
    redDark,
    darkYellow,
    acitveColor,
    acitveBorder,
    lightBlue,
    blackmedium,
  } = useSelector((state) => state.theme);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [bookingType, setBookingType] = useState("new");
  const [selectedProviderData, setSelectedProviderData] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getCustomerBooking(bookingType);
        setBookings(res?.response || []);
        setError(null);
      } catch (err) {
        setError(err.message);
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [bookingType]);

  const handleOpenProviderModal = (provider) => {
    setSelectedProviderData(provider);
    if (provider) {
      onOpen();
    }
  };

  const handleTabChange = (index) => {
    setTabIndex(index);
    // Map tab index to booking type
    const bookingTypes = ["new", "in_progress", "completed", "cancel"];
    setBookingType(bookingTypes[index]);
  };

  if (loading) return <SpinnerComponent />;

  const renderBookingCard = (booking) => (
    <Box
      onClick={() => handleOpenProviderModal(booking)}
      key={booking.id}
      className="bookingCard"
    >
      <Box
        border="1px"
        className="bookingCardInner"
        borderColor={acitveBorder}
        borderRadius="md"
        p={4}
        bg={lightBlue}
        _hover={{
          boxShadow:
            "rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px",
        }}
      >
        <Flex mb={4} gap="10px" justifyContent="space-between">
          <Flex>
            <Image
              boxSize="50px"
              borderRadius="full"
              src={booking?.booking_services[0]?.service_image}
              alt="Service Image"
              mr={4}
            />
            <Box flex="1">
              <Box color={blackmedium}>
                <Text fontWeight="bold" color={bodyTextColor}>
                  {booking?.booking_services[0]?.service_name}
                </Text>
                <Rating
                  total_rating={booking?.business_rating}
                  reviewsCount={booking?.business_review}
                />
              </Box>
            </Box>
          </Flex>
        </Flex>
        <Stack spacing={2} mb={4}>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap="5px"
          >
            <Flex gap="5px" alignItems="center">
              <Text fontSize="sm" color={blackmedium}>
                <Text fontWeight="600" as="span">
                  {" "}
                  ID :
                </Text>{" "}
                <Text
                  as="span"
                  fontWeight="400"
                  color={blackmedium}
                >
                  {booking?.order_no}
                </Text>
              </Text>
            </Flex>
            <Flex gap="5px" alignItems="center">
              <Icon
                as={FaCalendarAlt}
                mr={2}
                color={secondryText}
              />
              <Text color={blackmedium}>{booking?.booking_date}</Text>
            </Flex>
          </Flex>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap="5px"
          >
            <Flex gap="5px" alignItems="center">
              <Icon as={FaUser} mr={2} color={secondryText} />
              <Text color={blackmedium}>
                Manpower: {booking?.booking_services[0]?.qty || 0}
              </Text>
            </Flex>
            <Flex gap="5px" alignItems="center">
              <Icon
                as={FaMapMarkerAlt}
                mr={2}
                color={secondryText}
              />
              <Text color={blackmedium}>
                {formatAddress(booking?.booking_address)}
              </Text>
            </Flex>
          </Flex>
        </Stack>
        
        {/* Render conditional elements based on booking status */}
        {bookingType === "in_progress" && booking?.booking_otp?.start_time_otp && (
          <Text color="#000" fontWeight="700" mb="3">
            Start OTP:{" "}
            <Text as="span" color="red">
              {booking?.booking_otp?.start_time_otp}
            </Text>
          </Text>
        )}
        
        {bookingType === "job-in" && booking?.booking_otp?.end_time_otp && (
          <Text color="#000" fontWeight="700" mb="3">
            End OTP:{" "}
            <Text as="span" color="red">
              {booking?.booking_otp?.end_time_otp}
            </Text>
          </Text>
        )}
        
        <Flex gap="10px" mt="10px">
          {bookingType === "job-in" && (
            <Button colorScheme="blue" flex="1">
              Chat Now
            </Button>
          )}
          <Button colorScheme="green" flex="1">
            View
          </Button>
        </Flex>
      </Box>
    </Box>
  );

  return (
    <Box p={4} className="bookingHistoryPage">
      <Container maxW="container.xl" p={4} color={footerColor}>
        <Box>
          <Tabs index={tabIndex} onChange={handleTabChange}>
            <Box className="" border>
              <TabList
                className="tabsList"
                mx="auto"
                borderRadius="6px"
                border="0"
                gap="8px"
                p="8px"
                justifyContent="center"
                maxW="540px"
                bg={activeBg}
                flexWrap="wrap"
                fontSize={{ base: "14px", sm: "16px" }}
              >
                <Tab
                  border="0"
                  flex="1"
                  fontSize={{ base: "14px", sm: "16px" }}
                >
                  New Booking
                </Tab>
                <Tab
                  border="0"
                  flex="1"
                  fontSize={{ base: "14px", sm: "16px" }}
                >
                  In Progress
                </Tab>
                <Tab
                  border="0"
                  flex="1"
                  fontSize={{ base: "14px", sm: "16px" }}
                >
                  Completed
                </Tab>
                <Tab
                  border="0"
                  flex="1"
                  fontSize={{ base: "14px", sm: "16px" }}
                >
                  Cancelled
                </Tab>
              </TabList>
            </Box>

            <TabPanels>
              {/* New Bookings Tab */}
              <TabPanel py="30px" px="0">
                <SimpleGrid
                  columns={{ base: 1, sm: 2, md: 3, xl: 4 }}
                  spacing={4}
                  className="bookingCardWr"
                >
                  {bookings.length > 0 ? (
                    bookings.map((booking) => renderBookingCard(booking))
                  ) : (
                    <Box gridColumn="1 / -1">
                      <NodataFound message="No new bookings found" />
                    </Box>
                  )}
                </SimpleGrid>
              </TabPanel>

              {/* In Progress Tab */}
              <TabPanel py="30px" px="0">
                <SimpleGrid
                  columns={{ base: 1, sm: 2, md: 3, xl: 4 }}
                  spacing={4}
                  className="bookingCardWr"
                >
                  {bookings.length > 0 ? (
                    bookings.map((booking) => renderBookingCard(booking))
                  ) : (
                    <Box gridColumn="1 / -1">
                      <NodataFound message="No in-progress bookings found" />
                    </Box>
                  )}
                </SimpleGrid>
              </TabPanel>

              {/* Completed Tab */}
              <TabPanel py="30px" px="0">
                <SimpleGrid
                  columns={{ base: 1, sm: 2, md: 3, xl: 4 }}
                  spacing={4}
                  className="bookingCardWr"
                >
                  {bookings.length > 0 ? (
                    bookings.map((booking) => renderBookingCard(booking))
                  ) : (
                    <Box gridColumn="1 / -1">
                      <NodataFound message="No completed bookings found" />
                    </Box>
                  )}
                </SimpleGrid>
              </TabPanel>

              {/* Cancelled Tab */}
              <TabPanel py="30px" px="0">
                <SimpleGrid
                  columns={{ base: 1, sm: 2, md: 3, xl: 4 }}
                  spacing={4}
                  className="bookingCardWr"
                >
                  {bookings.length > 0 ? (
                    bookings.map((booking) => renderBookingCard(booking))
                  ) : (
                    <Box gridColumn="1 / -1">
                      <NodataFound message="No cancelled bookings found" />
                    </Box>
                  )}
                </SimpleGrid>
              </TabPanel>
            </TabPanels>
          </Tabs>
          
          {/* Details Modal */}
          {isOpen && (
            <BookingDetails
              isOpen={isOpen}
              onClose={onClose}
              providerData={selectedProviderData}
              bookingStatus={bookingType}
            />
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default CustomerBookingHistory;